"use client";

import React, { memo, useEffect } from "react";
import {
  useLGSSettingsDialog,
  useLGSSettingsPendingCoords,
} from "modules/lgs/stores/useLGSSettingsStore";
import { useUserLGSs } from "modules/lgs/hooks/useUserLGSs";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";
import { Modal } from "modules/common/components/Modal/Modal";
import RenderMapSearchAutocomplete from "modules/common/components/map/RenderMapSearchAutocomplete";
import { SelectLGSsForm } from "modules/lgs/components/SelectLGSsForm";
import type { ShortLGS } from "modules/common/model/models";
import { useMapSearchParams } from "modules/common/components/map/hooks/useMapSearchParams";
import { useQueryClient } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { SOFIA_LAT, SOFIA_LNG } from "modules/lgs/hooks/useUserLocation";

interface GeoResponse {
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  country: string | null;
  cityName: string | null;
  population: number | null;
}

// Define the memoized content component outside the main component
const LGSContent = memo(({ userLgs }: { userLgs: ShortLGS[] }) => {
  return (
    <div className="w-full">
      {userLgs && (
        <SelectLGSsForm userLgs={userLgs}>
          <RenderMapSearchAutocomplete />
        </SelectLGSsForm>
      )}
    </div>
  );
});

// Add display name for debugging purposes
LGSContent.displayName = "StoresContent";

export function SelectLGSDialog({
  initialCoords,
}: {
  initialCoords?: [longitude: number, latitude: number];
}) {
  const { userId, isUserLoaded } = useAppUser();
  const { isOpen, setOpen } = useLGSSettingsDialog();
  const { pendingCoords, clearPendingCoords } = useLGSSettingsPendingCoords();
  const { lat, lng, setParams } = useMapSearchParams();
  const queryClient = useQueryClient();

  const { userLgs, isSuccess } = useUserLGSs(isUserLoaded ? userId! : "");
  // SelectLGSsForm captures its first prop as the persistence baseline. Wait
  // for this client-only caller's query to succeed so an error cannot expose
  // an empty form that could overwrite the user's real stores.
  const isUserLgsReady = isSuccess;
  const handleClose = () => setOpen(false);

  // Sync location when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    if (pendingCoords) {
      // Explicit coords from caller (e.g., useQuickJoinStore on a store page)
      setParams(
        { lat: pendingCoords[1], lng: pendingCoords[0] },
        { history: "replace" }
      );
      clearPendingCoords();
    } else if (lat === SOFIA_LAT && lng === SOFIA_LNG) {
      if (userLgs.length > 0) {
        // Still at Sofia default and user has stores → center on first store
        setParams(
          { lat: userLgs[0].latitude, lng: userLgs[0].longitude },
          { history: "replace" }
        );
      } else {
        // The request-routed homepage already knows the visitor's city and no
        // longer runs a mount-time /api/geo query. Prefer those server-resolved
        // coordinates there; other routes retain the existing cached fallback.
        if (initialCoords) {
          setParams(
            { lat: initialCoords[1], lng: initialCoords[0] },
            { history: "replace" }
          );
        } else {
          const geoData = queryClient.getQueryData<GeoResponse>([
            QUERIES.GEO_DETECT,
          ]);
          if (geoData?.latitude != null && geoData?.longitude != null) {
            setParams(
              { lat: geoData.latitude, lng: geoData.longitude },
              { history: "replace" }
            );
          }
        }
      }
    }
    // Otherwise: URL already has meaningful coords (user searched or page set them)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Select your stores"
      maxWidth="6xl"
      height="auto"
      actions={<button className="btn btn-primary">Close</button>}
    >
      {isUserLgsReady && <LGSContent userLgs={userLgs} />}
    </Modal>
  );
}
